/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { withErrorBoundary } from '@navi-app/utils';
import { useStyletron, withStyle } from 'baseui';
import { Button, KIND } from 'baseui/button';
import { TriangleDown } from 'baseui/icon';
import { StatefulMenu, StyledEmptyState, StyledList } from 'baseui/menu';
import { Pagination } from 'baseui/pagination';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { Select, StyledDropdownListItem } from 'baseui/select';
import { LabelSmall } from 'baseui/typography';
import { Children, forwardRef, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ViewportList } from 'react-viewport-list';
import { DEFAULT_LIMIT, DEFAULT_PAGE, OPTIONS_LIMIT } from './constants/table';
import { TPagination } from './types/table';

export type TPropsPagination = {
  pagination?: TPagination;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  syncWithUrl?: boolean;
  limitLabel?: string;
  prevButtonLabel?: string;
  nextButtonLabel?: string;
  prepositionLabel?: string;
  defaultLimit?: number;
  defaultPage?: number;
  optionsLimit?: number[];
  /** Set to false to hide the `dari total N data` count next to the limit picker. */
  showTotalData?: boolean;
  /** Label before the page picker. Pass `''` to render the picker on its own. */
  pageLabel?: string;
};

const LIST_ITEM_HEIGHT = 36;
const EMPTY_LIST_HEIGHT = 48;
const MAX_LIST_HEIGHT = 200;

const ListItem = withStyle(StyledDropdownListItem, {
  paddingTop: 0,
  paddingBottom: 0,
  display: 'flex',
  alignItems: 'center',
});

const VirtualDropdown = forwardRef<HTMLUListElement, any>((props: any, ref) => {
  const children = Children.toArray(props.children);

  // @ts-ignore
  if (!children[0] || !children[0].props.item) {
    return (
      <StyledList $style={{ height: EMPTY_LIST_HEIGHT + 'px' }} ref={ref}>
        <StyledEmptyState
          // @ts-ignore
          {...children[0]?.props}
        />
      </StyledList>
    );
  }

  const height = Math.min(MAX_LIST_HEIGHT, children.length * LIST_ITEM_HEIGHT);

  return (
    <StyledList $style={{ height: height + 'px' }} ref={ref}>
      <ViewportList
        // @ts-ignore
        viewportRef={ref}
        items={children}
      >
        {(list) => {
          // @ts-ignore
          const { item, overrides, ...restChildProps } = list?.props || {};
          return (
            <ListItem
              key={item?.id}
              style={{
                boxSizing: 'border-box',
                height: LIST_ITEM_HEIGHT,
              }}
              {...restChildProps}
            >
              {item?.id}
            </ListItem>
          );
        }}
      </ViewportList>
    </StyledList>
  );
});

function PaginationTable({
  pagination,
  onPageChange,
  onLimitChange,
  syncWithUrl = true,
  limitLabel = 'Baris per halaman',
  prevButtonLabel = 'Sebelumnya',
  nextButtonLabel = 'Selanjutnya',
  prepositionLabel = 'dari',
  defaultLimit = DEFAULT_LIMIT,
  defaultPage = DEFAULT_PAGE,
  optionsLimit = OPTIONS_LIMIT,
  showTotalData = true,
  pageLabel = 'Halaman: ',
}: TPropsPagination) {
  const [css] = useStyletron();
  const [searchParams, setSearchParams] = useSearchParams();

  // Hide textual labels while keeping arrows; avoid BaseUI default by using NBSP
  const computedPrevLabel =
    prevButtonLabel && prevButtonLabel.trim() !== ''
      ? prevButtonLabel
      : '\u00A0';
  const computedNextLabel =
    nextButtonLabel && nextButtonLabel.trim() !== ''
      ? nextButtonLabel
      : '\u00A0';

  const [limit, setLimit] = useState(pagination?.limit || DEFAULT_LIMIT);
  const [page, setPage] = useState(pagination?.currentPage || DEFAULT_PAGE);
  const [totalPage, setTotalPage] = useState(pagination?.totalPage || 0);
  const [totalData, setTotalData] = useState(pagination?.total || 0);
  const [selectedPage, setSelectedPage] = useState([
    {
      label: '1',
      id: '1',
    },
  ]);

  const optionsPage = useMemo(() => {
    return Array.from({ length: totalPage }, (_, index) => ({
      label: (index + 1).toString(),
      id: (index + 1).toString(),
    }));
  }, [totalPage]);

  useEffect(() => {
    setSelectedPage([{ id: page?.toString(), label: page?.toString() }]);
  }, [page]);

  useEffect(() => {
    if (pagination?.currentPage) {
      setPage(pagination?.currentPage);
    }
    if (pagination?.limit) {
      setLimit(pagination?.limit);
    }
  }, [pagination?.currentPage, pagination?.limit]);

  useEffect(() => {
    if (pagination?.total) setTotalData(pagination?.total);
  }, [pagination?.total]);

  useEffect(() => {
    if (!pagination?.totalPage) {
      setTotalPage(Math.ceil(totalData / limit));
    } else {
      setTotalPage(pagination?.totalPage);
    }
  }, [pagination, totalData, limit]);

  // Ensure that 'limit' and 'page' values from the URL are valid or set defaults
  // Update both the URL parameters and component state accordingly
  useEffect(() => {
    if (!syncWithUrl) {
      setLimit(pagination?.limit || defaultLimit);
      setPage(pagination?.currentPage || defaultPage);
      return;
    }

    const urlLimit = searchParams.get('limit') || defaultLimit;
    const urlPage = searchParams.get('page') || defaultPage;

    const isValidLimit = optionsLimit.includes(Number(urlLimit));
    const isValidPage = Number(urlPage) <= totalPage;

    const currentLimit = isValidLimit ? Number(urlLimit) : defaultLimit;
    const currentPage = isValidPage ? Number(urlPage) : defaultPage;

    const shouldUpdateParams =
      searchParams.get('limit') !== currentLimit.toString() ||
      searchParams.get('page') !== currentPage.toString();

    setLimit(currentLimit);
    setPage(currentPage);

    if (shouldUpdateParams) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('limit', currentLimit.toString());
      nextSearchParams.set('page', currentPage.toString());
      setSearchParams(nextSearchParams);
    }
  }, [
    syncWithUrl,
    pagination?.limit,
    pagination?.currentPage,
    defaultLimit,
    defaultPage,
    searchParams,
    optionsLimit,
    setSearchParams,
    totalPage,
  ]);

  const handlePageChange = (nextPage: number) => {
    if (onPageChange) onPageChange(nextPage);
    if (syncWithUrl) {
      searchParams.set('page', nextPage?.toString());
      setSearchParams(searchParams);
    }
    setPage(nextPage);
    if (nextPage < 1) {
      return;
    }
    if (nextPage > Math.ceil(totalPage / limit)) {
      return;
    }
  };

  const handleLimitChange = (nextLimit: number) => {
    if (onLimitChange) onLimitChange(nextLimit);
    if (syncWithUrl) {
      searchParams.set('limit', nextLimit.toString());
      searchParams.set('page', DEFAULT_PAGE.toString());
      setSearchParams(searchParams);
    }
    const nextPageNum = Math.ceil(totalPage / nextLimit);
    if (nextPageNum < page) {
      setLimit(nextLimit);
      setPage(nextPageNum);
    } else {
      setLimit(nextLimit);
    }
  };

  return (
    <div
      className={css({
        paddingTop: '8px',
        display: 'flex',
        justifyContent: 'space-between',
      })}
    >
      <div className="flex items-center gap-2">
        {limitLabel && <LabelSmall>{limitLabel}</LabelSmall>}
        <StatefulPopover
          popoverMargin={0}
          content={({ close }) => (
            <StatefulMenu
              items={optionsLimit.map((limit: number) => ({
                label: limit,
                value: limit,
              }))}
              onItemSelect={({ item }) => {
                handleLimitChange(item.label);
                close();
              }}
              overrides={{
                List: {
                  style: { width: '64px' },
                },
              }}
            />
          )}
          placement={PLACEMENT.bottom}
          overrides={{
            Body: {
              style: { zIndex: 100 },
            },
          }}
        >
          <Button
            kind={KIND.secondary}
            size="compact"
            endEnhancer={TriangleDown}
          >
            {limit}
          </Button>
        </StatefulPopover>
        {showTotalData && <LabelSmall>dari total {totalData} data</LabelSmall>}
      </div>

      <Pagination
        size="compact"
        numPages={totalPage}
        currentPage={page}
        onPageChange={({ nextPage }) => handlePageChange(nextPage)}
        labels={{
          prevButton: computedPrevLabel,
          nextButton: computedNextLabel,
          preposition: prepositionLabel,
        }}
        overrides={{
          DropdownContainer: {
            style: {
              outline: 'none !important',
            },
          },
          PrevButton: {
            style: {
              display: page === 1 ? 'none' : 'flex',
              padding: '8px 0px',
              ':hover': {
                backgroundColor: 'transparent',
              },
              ':active': {
                backgroundColor: 'transparent',
              },
            },
          },
          NextButton: {
            style: {
              display: page === totalPage ? 'none' : 'flex',
              padding: '8px 0px',
              ':hover': {
                backgroundColor: 'transparent',
              },
              ':active': {
                backgroundColor: 'transparent',
              },
            },
          },
          MaxLabel: {
            style: ({ $theme }) => ({
              ...$theme.typography.LabelSmall,
            }),
          },
          Select: {
            props: {
              overrides: {
                ControlContainer: {
                  component: () => (
                    <div className="flex items-center gap-3">
                      {pageLabel && <LabelSmall>{pageLabel}</LabelSmall>}
                      <Select
                        clearable={false}
                        size="compact"
                        options={optionsPage}
                        value={selectedPage}
                        labelKey="id"
                        valueKey="label"
                        noResultsMsg=" "
                        onChange={({ value }) =>
                          handlePageChange(Number(value[0].id))
                        }
                        overrides={{
                          Dropdown: {
                            component: VirtualDropdown,
                          },
                          Popover: {
                            props: {
                              overrides: {
                                Body: {
                                  style: { zIndex: 100 },
                                },
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  ),
                },
                SingleValue: {
                  style: ({ $theme }: any) => ({
                    padding: 0,
                    marginTop: '4px',
                    marginRight: '8px',
                    color: $theme.colors.buttonTertiaryText,
                    ...$theme.typography.LabelSmall,
                  }),
                },
                SelectArrow: {
                  style: ({ $theme }: any) => ({
                    padding: 0,
                    color: $theme.colors.buttonTertiaryText,
                  }),
                },
              },
            },
          },
        }}
      />
    </div>
  );
}

export default withErrorBoundary(PaginationTable);
